<script>
  import { createEventDispatcher, tick } from "svelte";
  import { Modal } from "svelte-utilities";
  import { multiplier } from "@local/extensions/utilities/scales.js";

  const dispatch = createEventDispatcher();

  let modal;
  let customScale = 20;
  let input;

  $: parsedScale = parseFloat(customScale);
  $: isValid = Number.isFinite(parsedScale) && parsedScale > 0 && parsedScale <= 2000;

  function setScale() {
    if (!isValid) return;

    dispatch("setScale", { value: `1:${customScale}` });
  }

  // The caller's current scale, as the ratio this popup edits: a custom
  // "1:X" prefills X, and a standard scale (e.g. "1/4in:1ft") prefills its
  // numeric ratio — display only; nothing is emitted unless the user
  // confirms, so opening and closing never relabels a standard scale as
  // custom. Anything unparseable or outside the popup's own 0 < N <= 2000
  // validation falls back to the historical default of 20.
  function prefillFrom(current) {
    if (typeof current === "string" && current.trim()) {
      try {
        const m = multiplier(current);
        if (Number.isFinite(m) && m > 0 && m <= 2000) return String(m);
      } catch {
        // Not a scale string ("__na__", "Mixed", …) — use the default.
      }
    }
    return 20;
  }

  export const open = async (current = null) => {
    customScale = prefillFrom(current);
    modal.open();
    await tick();
    input.focus();
  };
</script>

<Modal
  bind:this={modal}
  closeable
  on:confirm={setScale}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Set Custom Scale", type: "confirm", style: "primary", disabled: !isValid },
  ]}>
  <div slot="title">Set Custom Scale</div>
  <div slot="content" class="space-y-2 text-sm">
    <div class="flex items-center gap-2 justify-center">
      <input disabled value={1} />
      <div>:</div>
      <input bind:value={customScale} bind:this={input} />
    </div>
    <div class="text-red-500 text-center text-xs">
      {#if !isValid}
        Please enter a valid number between 0 and 2000
      {:else}
        &nbsp;
      {/if}
    </div>
  </div>
</Modal>

<style>
  input {
    @apply border p-1 rounded w-12;
  }
</style>
